/**
 * Fold the flat audit log into one row per verification.
 *
 * The API returns one entry per event, and deliberately keeps doing so: a
 * mutation returns the single row it just wrote, and the page merges that into
 * its history without refetching. Grouping on the server as well would mean two
 * implementations of the same fold, so it happens here, once, over whatever
 * events the page currently holds.
 *
 * Pure and dependency-free so it can be unit tested without a render.
 */

/** Events that mean the advisor entered the wrong code. Counted per session. */
const WRONG_CODE = 'verify_failed_wrong_code';

/**
 * Events that describe a send that never happened.
 *
 * These carry no verification_id, because no verification was created - there
 * was nothing to send to, the number was opted out, the address was refused, or
 * the rate limit bit. Each is its own single-event row rather than being folded
 * into the neighbouring session, so a blocked attempt stays visible instead of
 * being hidden under a later success.
 */
export const BLOCKED_EVENTS = new Set([
  'code_send_blocked_no_phone',
  'code_send_blocked_opted_out',
  'code_send_blocked_no_destination',
  'code_send_blocked_destination',
  'code_send_rate_limited',
  'code_send_failed'
]);

/** Milliseconds for an event timestamp; 0 for anything unparseable. */
const timeOf = (event) => {
  const parsed = Date.parse(event?.created_at);
  return Number.isNaN(parsed) ? 0 : parsed;
};

/**
 * Order two events oldest-first.
 *
 * Timestamps are only millisecond-resolution, so two events in one session can
 * tie - and a tie decided by array order would pick whichever the page happened
 * to prepend, showing "Code sent" as the outcome of a session that has since
 * been answered. event_sk breaks it: the backend builds it as
 * "<created_at>#<event_id>", so it is unique, and it is the order DynamoDB
 * itself returns these rows in.
 */
const byOccurrence = (a, b) => {
  const delta = timeOf(a) - timeOf(b);
  if (delta !== 0) return delta;
  return String(a?.event_sk || '').localeCompare(String(b?.event_sk || ''));
};

/**
 * The key a session is grouped under.
 *
 * verification_id when there is one. Otherwise the event stands alone, keyed by
 * its own id - never by a falsy shared value, which would collapse every
 * blocked send in the list into a single row.
 */
const sessionKey = (event) => event?.verification_id || `event:${event?.event_id}`;

/**
 * Group audit events into sessions, newest first.
 *
 * @param {Array<object>} events Audit events in any order.
 * @returns {Array<object>} One entry per verification, each carrying the latest
 *   event's outcome, the shared note, and the attempt count.
 */
export const groupEventsIntoSessions = (events = []) => {
  const sessions = new Map();

  events.forEach((event) => {
    if (!event || !event.event_id) return;
    const key = sessionKey(event);
    const existing = sessions.get(key);
    if (existing) {
      existing.events.push(event);
    } else {
      sessions.set(key, { key, events: [event] });
    }
  });

  const rows = Array.from(sessions.values()).map((session) => {
    // Sorted rather than assumed: the list is newest-first from the API but the
    // page prepends each new event as it happens, and a session that spans a
    // pagination boundary arrives in neither order reliably.
    const ordered = [...session.events].sort(byOccurrence);
    const first = ordered[0];
    const latest = ordered[ordered.length - 1];

    // Taken from the earliest event that carries each, so the send's details
    // survive: the terminal event records the outcome, not who it went to.
    const firstWith = (field) => ordered.find((e) => e[field])?.[field] || null;

    return {
      key: session.key,
      verificationId: latest.verification_id || null,
      latestEvent: latest,
      eventType: latest.event_type,
      createdAt: latest.created_at,
      startedAt: first.created_at,
      clientName: firstWith('client_name'),
      channel: firstWith('channel') || (
        String(firstWith('phone_source') || '').toLowerCase().startsWith('email')
          ? 'email'
          : (firstWith('phone_source') || firstWith('phone_last4') ? 'phone' : null)
      ),
      destinationHint: firstWith('destination_hint') || firstWith('phone_last4'),
      staffDisplayName:
        firstWith('staff_display_name') || firstWith('staff_username') || firstWith('staff_user_id'),
      reason: firstWith('reason'),
      attempts: Math.max(
        ordered.filter((e) => e.event_type === WRONG_CODE).length,
        ...ordered
          .filter((e) => e.event_type === 'verify_failed_attempts_exceeded')
          .map((e) => Number(e.attempt_number) || 0)
      ),
      blocked: BLOCKED_EVENTS.has(latest.event_type),
      events: ordered
    };
  });

  // Newest session first, matching the order the events arrive in. Ties break
  // on key so the order is stable across re-renders rather than depending on
  // Map insertion, which changes as events are prepended.
  return rows.sort((a, b) => {
    const delta = byOccurrence(b.latestEvent, a.latestEvent);
    return delta !== 0 ? delta : a.key.localeCompare(b.key);
  });
};

export default groupEventsIntoSessions;
